import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Navbar,
  NavbarBackLink,
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
} from 'konsta/react'
import {
  TrendingUp,
  Trophy,
  Target,
  Clock,
  DollarSign,
  BarChart3,
  Percent,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

type Tab = 'overview' | 'week' | 'month'

const tabLabels: Record<Tab, string> = {
  overview: '總覽',
  week: '本週',
  month: '本月',
}

const statsData: Record<Tab, { games: number; winRate: number; netPnl: number; biggestWin: number; avgTime: string; avgPnl: number; vpip: number; pfr: number }> = {
  overview: { games: 128, winRate: 62, netPnl: 245000, biggestWin: 120000, avgTime: '45分鐘', avgPnl: 1914, vpip: 28, pfr: 22 },
  week:     { games: 14, winRate: 71, netPnl: 58000, biggestWin: 32000, avgTime: '38分鐘', avgPnl: 4143, vpip: 31, pfr: 25 },
  month:    { games: 52, winRate: 65, netPnl: 128000, biggestWin: 72000, avgTime: '42分鐘', avgPnl: 2462, vpip: 29, pfr: 23 },
}

const handDistribution = [
  { name: '高牌', pct: '42%' },
  { name: '一對', pct: '35%' },
  { name: '兩對', pct: '12%' },
  { name: '三條', pct: '6%' },
  { name: '順子', pct: '3%' },
  { name: '同花', pct: '1.5%' },
  { name: '葫蘆', pct: '0.4%' },
  { name: '四條', pct: '0.08%' },
  { name: '同花順', pct: '0.02%' },
]

export function StatisticsScreen() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const data = statsData[tab]

  return (
    <Page className="bg-[#F2F2F7]">
      <Navbar
        title="統計數據"
        left={<NavbarBackLink onClick={() => navigate(-1)} />}
      />

      <Block className="!mt-4 !mb-2">
        <Segmented strong rounded>
          {(Object.keys(tabLabels) as Tab[]).map((key) => (
            <SegmentedButton
              key={key}
              active={tab === key}
              onClick={() => setTab(key)}
            >
              {tabLabels[key]}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      <BlockTitle>核心數據</BlockTitle>
      <div className="grid grid-cols-2 gap-3 px-4">
        <StatCard icon={<Target className="w-5 h-5 text-[#007AFF]" />} label="總場次" value={String(data.games)} />
        <StatCard icon={<Percent className="w-5 h-5 text-[#007AFF]" />} label="勝率" value={`${data.winRate}%`} />
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-[#34C759]" />}
          label="淨盈虧"
          value={`${data.netPnl >= 0 ? '+' : ''}${formatCurrency(data.netPnl)}`}
          valueColor={data.netPnl >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}
        />
        <StatCard icon={<Trophy className="w-5 h-5 text-amber-500" />} label="最大贏取" value={formatCurrency(data.biggestWin)} />
      </div>

      <BlockTitle className="!mt-6">場次統計</BlockTitle>
      <List strongIos insetIos>
        <ListItem
          title="平均每場時間"
          after={<span className="text-[#8E8E93]">{data.avgTime}</span>}
          media={<Clock className="w-5 h-5 text-[#007AFF]" />}
        />
        <ListItem
          title="平均每場盈虧"
          after={
            <span className={data.avgPnl >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}>
              {data.avgPnl >= 0 ? '+' : ''}{formatCurrency(data.avgPnl)}
            </span>
          }
          media={<DollarSign className="w-5 h-5 text-[#34C759]" />}
        />
        <ListItem
          title="VPIP"
          after={<span className="text-[#8E8E93]">{data.vpip}%</span>}
          media={<BarChart3 className="w-5 h-5 text-purple-500" />}
        />
        <ListItem
          title="PFR"
          after={<span className="text-[#8E8E93]">{data.pfr}%</span>}
          media={<BarChart3 className="w-5 h-5 text-orange-500" />}
        />
      </List>

      <BlockTitle className="!mt-6">牌型分布</BlockTitle>
      <List strongIos insetIos>
        {handDistribution.map((h) => (
          <ListItem
            key={h.name}
            title={h.name}
            after={<span className="text-[#8E8E93] font-medium">{h.pct}</span>}
          />
        ))}
      </List>
    </Page>
  )
}

function StatCard({
  icon,
  label,
  value,
  valueColor = 'text-black',
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueColor?: string
}) {
  return (
    <div className="bg-white rounded-xl p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs text-[#8E8E93] font-medium">{label}</span>
      </div>
      <span className={`text-xl font-bold ${valueColor}`}>{value}</span>
    </div>
  )
}
